import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useState } from 'react';

import { useForm } from 'react-hook-form';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { Company, CompanyUpdateApiDTO, ConstantArray, getCompanyUpdateApiSchema, Paths } from '_lib/api/apiTypes.shim';
import { AuthUser } from '_lib/featureComponents/auth/auth.types';
import {
  ensureKeys,
  extractAllErrors,
  extractUnmappableErrors,
  pickDeeplyNested,
  replaceEmptyStringsWithNull
} from '_lib/utils/helpers';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';
import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { TabChangeObj } from '../../CompanyUpdate';

export type UseCompanyUpdateFormOptions = {
  /* TODO: remove the 'Company' path allowance when the DTO is fixed */
  formValuePaths: ConstantArray<Paths<CompanyUpdateApiDTO>> | ConstantArray<Paths<Company>>;
  company: Company;
  authUser: AuthUser;
  tabChangeObj: TabChangeObj;
  snackMessage?: string;
};

export const useCompanyUpdateForm = ({
  company,
  authUser,
  formValuePaths,
  tabChangeObj,
  snackMessage
}: UseCompanyUpdateFormOptions) => {
  const [validationErrors, setValidationErrors] = useState<string[]>();
  const [allErrors, setAllErrors] = useState<string[]>();

  const { enqueueSnackbar } = useOurSnackbar();
  const api = useContext(ApiContext);
  const companyPutMutation = api.companies.usePut();

  const companyUpdateFormContext = useForm<CompanyUpdateApiDTO>({
    context: { authUser },
    resolver: (values, context: { authUser: AuthUser }, options) => {
      const schema = getCompanyUpdateApiSchema(context.authUser);
      const resolver = zodResolver(schema);
      const filteredValues = pickDeeplyNested(values, formValuePaths);
      const normalizedValues = replaceEmptyStringsWithNull(filteredValues);
      return resolver(normalizedValues as typeof filteredValues, context, { ...options });
    },
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: ensureKeys(company, formValuePaths)
  });

  const handleSave = apiSubmitHandler(
    companyUpdateFormContext.handleSubmit,
    companyUpdateFormContext.setError,
    async (data, { setFormErrors }) => {
      setValidationErrors(undefined);
      setAllErrors(undefined);
      try {
        await companyPutMutation.put({ id: company.id, data });
        if (snackMessage) enqueueSnackbar(snackMessage);
        companyUpdateFormContext.reset(data);
      } catch (err) {
        if (err instanceof BadRequestError) {
          setValidationErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    },
    {
      onInvalid: (errors) => {
        enqueueSnackbar('Validation Error occurred. Please check inputs and try again', { variant: 'error' });
        // Extract all errors that are not mappable to fields in this form
        const unmappableErrors = extractUnmappableErrors(errors, formValuePaths);
        if (unmappableErrors.length > 0) {
          setValidationErrors(unmappableErrors);
        } else {
          setValidationErrors(undefined);
        }
        const allErrors = extractAllErrors(errors);
        setAllErrors(allErrors);
        logDebug(errors);
      }
    }
  );

  tabChangeObj.onSave = handleSave;
  tabChangeObj.isValid = companyUpdateFormContext.formState.isValid;
  tabChangeObj.isDirty = companyUpdateFormContext.formState.isDirty;
  tabChangeObj.isTouched = Object.keys(companyUpdateFormContext.formState.touchedFields).length > 0;

  return {
    companyUpdateFormContext,
    validationErrors,
    allErrors,
    handleSave,
    isPending: companyPutMutation.isPending
  };
};
